import { SEASON_DEFINITIONS } from '@brick-rogue/game-content';
import type { SeasonChallengeId, SeasonDefinition, SeasonId } from '@brick-rogue/game-core';

const DAY_MILLISECONDS = 24 * 60 * 60 * 1_000;

export type SeasonRotationStatus = 'archived' | 'active' | 'upcoming';

export interface SeasonRotationSnapshot {
  readonly seasonId: SeasonId;
  readonly seasonVersion: number;
  readonly rotationIndex: number;
  readonly challengeId: SeasonChallengeId;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly status: SeasonRotationStatus;
}

export function getSeasonRotations(
  season: SeasonDefinition,
  now: Date = new Date(),
): readonly SeasonRotationSnapshot[] {
  const { startMilliseconds, endMilliseconds, rotationMilliseconds } =
    validateSeasonDefinition(season);
  const nowMilliseconds = now.getTime();
  if (!Number.isFinite(nowMilliseconds)) {
    throw new Error('赛季时间无效');
  }

  return season.challengeIds.map((challengeId, rotationIndex) => {
    const rotationStart = startMilliseconds + rotationIndex * rotationMilliseconds;
    const rotationEnd = Math.min(endMilliseconds, rotationStart + rotationMilliseconds);
    const status: SeasonRotationStatus =
      nowMilliseconds < rotationStart
        ? 'upcoming'
        : nowMilliseconds >= rotationEnd
          ? 'archived'
          : 'active';
    return {
      seasonId: season.id,
      seasonVersion: season.version,
      rotationIndex,
      challengeId,
      startsAt: new Date(rotationStart).toISOString(),
      endsAt: new Date(rotationEnd).toISOString(),
      status,
    };
  });
}

export function getActiveSeasonRotation(now: Date = new Date()): SeasonRotationSnapshot | null {
  const activeRotations = Object.values(SEASON_DEFINITIONS).flatMap((season) =>
    getSeasonRotations(season, now).filter((rotation) => rotation.status === 'active'),
  );
  if (activeRotations.length > 1) {
    throw new Error('赛季目录存在重叠的活动轮换');
  }
  return activeRotations[0] ?? null;
}

export function validateSeasonDefinition(season: SeasonDefinition): {
  readonly startMilliseconds: number;
  readonly endMilliseconds: number;
  readonly rotationMilliseconds: number;
} {
  const startMilliseconds = Date.parse(season.startsAt);
  const endMilliseconds = Date.parse(season.endsAt);
  const rotationMilliseconds = season.rotationDurationDays * DAY_MILLISECONDS;
  if (
    !Number.isFinite(startMilliseconds) ||
    !Number.isFinite(endMilliseconds) ||
    endMilliseconds <= startMilliseconds ||
    !Number.isInteger(season.rotationDurationDays) ||
    season.rotationDurationDays <= 0 ||
    season.version <= 0 ||
    !Number.isInteger(season.version)
  ) {
    throw new Error(`赛季 ${season.id} 的版本或时间窗口无效`);
  }
  const expectedRotationCount = Math.ceil(
    (endMilliseconds - startMilliseconds) / rotationMilliseconds,
  );
  if (
    season.challengeIds.length !== expectedRotationCount ||
    new Set(season.challengeIds).size !== season.challengeIds.length
  ) {
    throw new Error(`赛季 ${season.id} 的轮换目录没有完整且唯一地覆盖有效期`);
  }
  return { startMilliseconds, endMilliseconds, rotationMilliseconds };
}
